import {installStudioBridge, uninstallStudioBridge} from '../studio/bridge.factory.js';

// Reuse the runtime callback on bfcache restores. The raw require remains inside
// this module closure; no require function or module cache is published globally.
const runtimeInitializers = new WeakMap();

function registerWithCapturedRuntime(window, initialize) {
  const initializeWithRuntime = runtimeInitializers.get(window);
  if (initializeWithRuntime) return initializeWithRuntime(initialize);
  const chunks = window.webpackChunk_N_E;
  if (!Array.isArray(chunks)) throw new Error('Captured editor runtime is not ready');
  chunks.push([['studio-owned-bridge'], {}, runtime => {
    runtimeInitializers.set(window, callback => callback(runtime));
    initialize(runtime);
  }]);
}

/**
 * Attach only after the native viewport/picker exists, when its dependencies
 * have loaded. Commands are exposed through the explicit bridge, not a React
 * object or a global runtime require. Injection keeps the startup testable.
 */
export function createBridgeConnection({
  window, adapter, registry,
  install = installStudioBridge,
  uninstall = uninstallStudioBridge,
  register = registerWithCapturedRuntime
}) {
  let attempted = false;
  let installed = false;
  let disposed = false;
  function reportError(error) {
    window.console.error('[Mockup Studio] Direct editor bridge failed', error);
    window.dispatchEvent(new window.CustomEvent('studio:local-error', {
      detail: {message: error.message}
    }));
  }
  return {
    connect(onChange) {
      if (attempted || disposed || !adapter.ready) return false;
      attempted = true;
      try {
        register(window, runtime => {
          if (disposed) return;
          try {
            const bridge = install(runtime, window);
            installed = true;
            adapter.connectBridge(bridge, registry, onChange);
          } catch (error) { reportError(error); }
        });
      } catch (error) {
        reportError(error);
      }
      return installed;
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      adapter.disconnectBridge();
      if (installed) uninstall(window);
      installed = false;
    }
  };
}
